import React, { useState, useEffect, useCallback } from 'react';
import {
  getHeroContent, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, AWS_URL, FaHeart, MdStore, MdList, getTabLabel, MdMenu
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component for proper editing and saving
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  // Get the current value from storeTheme first, then from props
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  // Update local value when storeTheme changes or when editing starts
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    // Save the current local value to parent
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    // Deactivate edit mode
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      // Then navigate if provided and not in modal
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    // Save on blur (when clicking outside)
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-slate-200 bg-white/90 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#c9a96e] focus:ring-2 focus:ring-[#c9a96e]/20";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  // Display the current value - force re-render when storeTheme changes
  const displayValue = p.storeTheme?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function ElysianLayout({ p }) {
  const T = p.storeTheme || {};

  const [isScrolled, setIsScrolled] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isHoveringHero, setIsHoveringHero] = useState(false);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  
  // Force re-render when storeTheme changes
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    // This will trigger re-render when storeTheme updates
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);
  
  // Model images array for hero section animation
  const modelImages = [
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/32e35743-1600-4df6-b1f9-2b8ef37d8b50_1.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/0c90a569-a6c5-4f23-aa26-3298172c41b5_2.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/76d80a10-f428-498e-a615-dd7b06cba415_3.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/a32c8344-da63-4169-854b-911b161c31c0_4.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/bb328c33-4d59-4a28-aaf3-48f4d59a0c68_5.jpg",
  ];
  
  // Blog posts data with editable fields
  const blogPosts = [
    {
      id: 1,
      titleKey: "blog1_title",
      title: "The Art of Layering",
      descKey: "blog1_desc",
      desc: "Discover how to master the art of layering this season with our expert styling tips. From lightweight kimonos to chunky knits, learn how to create depth and dimension in your outfits.",
      fullContentKey: "blog1_content",
      fullContent: "Layering is an art form that transforms simple pieces into extraordinary ensembles. In this comprehensive guide, we explore the nuances of fabric combinations, color harmonies, and silhouette play that define masterful layering.\n\nOur stylists share insider tips on transitioning your wardrobe through seasons, creating versatile looks that work from desk to dinner, and investing in key layering pieces that will serve you for years to come.\n\nFrom the perfect lightweight cardigan to the statement blazer that elevates any outfit, discover how to build a layering collection that expresses your unique style while maximizing functionality.",
      image:"https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/32e35743-1600-4df6-b1f9-2b8ef37d8b50_1.jpg",
      dateKey: "blog1_date",
      date: "March 15, 2024",
      authorKey: "blog1_author",
      author: "Isabella Rossi",
      readTimeKey: "blog1_readtime",
      readTime: "5 min read",
      categoryKey: "blog1_category",
      category: "Style Guide"
    },
    {
      id: 2,
      titleKey: "blog2_title",
      title: "Sustainable Luxury",
      descKey: "blog2_desc",
      desc: "How we're redefining luxury with sustainable practices and ethical sourcing. Our commitment to the planet without compromising on elegance.",
      fullContentKey: "blog2_content",
      fullContent: "True luxury should never come at the expense of our planet. At Elysian, we believe that sustainability and sophistication can coexist beautifully. Our journey toward ethical luxury encompasses every aspect of our production—from sourcing organic fabrics to implementing zero-waste cutting techniques.\n\nWe partner with artisans who share our vision, ensuring fair wages and safe working conditions. Our fabrics are carefully selected from mills that prioritize water conservation and renewable energy.\n\nThis commitment extends to our packaging, which is crafted from recycled materials and designed to be repurposed. Join us in redefining what luxury means—one that honors both people and planet.",
      image:"https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/0c90a569-a6c5-4f23-aa26-3298172c41b5_2.jpg",
      dateKey: "blog2_date",
      date: "March 10, 2024",
      authorKey: "blog2_author",
      author: "Marcus Chen",
      readTimeKey: "blog2_readtime",
      readTime: "4 min read",
      categoryKey: "blog2_category",
      category: "Sustainability"
    },
    {
      id: 3,
      titleKey: "blog3_title",
      title: "Behind the Seams",
      descKey: "blog3_desc",
      desc: "A glimpse into our atelier where every piece comes to life with love and care. Meet the artisans who bring our vision to reality.",
      fullContentKey: "blog3_content",
      fullContent: "Step inside our atelier, where creativity meets craftsmanship. Every Elysian garment begins as a sketch, evolves through countless iterations, and finally takes shape under the skilled hands of our master artisans.\n\nOur head designer, Elena Vasquez, walks us through the journey of a single dress—from concept to completion. With over 200 hours of handwork invested in each couture piece, our atelier is a sanctuary of dedication and passion.\n\nMeet the team behind the magic: Maria, our embroidery specialist who has been with us for 15 years; Ahmed, whose precision cutting defines our signature silhouettes; and young apprentice Sofia, who represents the future of our craft. Together, they ensure that every piece leaving our atelier carries the soul of true artistry.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/76d80a10-f428-498e-a615-dd7b06cba415_3.jpg",
      dateKey: "blog3_date",
      date: "March 5, 2024",
      authorKey: "blog3_author",
      author: "Elena Vasquez",
      readTimeKey: "blog3_readtime",
      readTime: "6 min read",
      categoryKey: "blog3_category",
      category: "Behind the Scenes"
    },
    {
      id: 4,
      titleKey: "blog4_title",
      title: "The Color Edit: Spring 2024",
      descKey: "blog4_desc",
      desc: "Explore the season's most captivating hues and learn how to incorporate them into your wardrobe.",
      fullContentKey: "blog4_content",
      fullContent: "Spring 2024 brings a palette of renewal and optimism. From ethereal lilacs to vibrant corals, this season's colors celebrate life and energy. Our color experts break down the key shades dominating the runways and show you how to wear them with confidence.\n\nDiscover which colors complement your skin tone, how to mix unexpected combinations, and which statement pieces will give you the most versatility. Whether you're drawn to soft pastels or bold brights, there's a shade waiting to express your mood.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/a32c8344-da63-4169-854b-911b161c31c0_4.jpg",
      dateKey: "blog4_date",
      date: "February 28, 2024",
      authorKey: "blog4_author",
      author: "Sophia Laurent",
      readTimeKey: "blog4_readtime",
      readTime: "4 min read",
      categoryKey: "blog4_category",
      category: "Trends"
    },
    {
      id: 5,
      titleKey: "blog5_title",
      title: "Evening Wear Essentials",
      descKey: "blog5_desc",
      desc: "From red carpet events to intimate galas, discover the gowns that make every entrance unforgettable.",
      fullContentKey: "blog5_content",
      fullContent: "The perfect evening gown is more than clothing—it's an armor of confidence. Our evening wear edit features silhouettes that flatter every figure, from classic A-lines to dramatic mermaid cuts.\n\nLearn about the fabrics that photograph beautifully, the necklines that elongate, and the details that transform a dress into a statement. Whether you're attending a black-tie wedding or a charity gala, we've curated the essential gowns that will make you feel like the best version of yourself.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/bb328c33-4d59-4a28-aaf3-48f4d59a0c68_5.jpg",
      dateKey: "blog5_date",
      date: "February 20, 2024",
      authorKey: "blog5_author",
      author: "Isabella Rossi",
      readTimeKey: "blog5_readtime",
      readTime: "5 min read",
      categoryKey: "blog5_category",
      category: "Evening Wear"
    }
  ];
  
  // Shop category cards with editable fields
  const shopCategories = [
    { key: "shop_new_arrivals", title: "New Arrivals", descKey: "shop_new_arrivals_desc", desc: "Discover the latest season", icon: "✨", color: "from-amber-500/20 to-purple-500/20" },
    { key: "shop_best_sellers", title: "Best Sellers", descKey: "shop_best_sellers_desc", desc: "Customer favorites", icon: "⭐", color: "from-purple-500/20 to-amber-500/20" },
    { key: "shop_limited_edition", title: "Limited Edition", descKey: "shop_limited_edition_desc", desc: "Exclusive pieces", icon: "💎", color: "from-amber-500/20 to-rose-500/20" },
  ];
  
  // Navigation items with editable fields
  const navItems = [
    { key: "nav_home", name: "Home", page: "home" },
    { key: "nav_shop", name: "Shop", page: "shop" },
    { key: "nav_collections", name: "Collections", page: "collections" },
    { key: "nav_journal", name: "Journal", page: "journal" },
    { key: "nav_about", name: "About", page: "about" },
  ];
  
  // Auto-rotate model images
  useEffect(() => {
    if (isHoveringHero) return;
    const interval = setInterval(() => {
      setActiveImageIndex((prev) => (prev + 1) % modelImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isHoveringHero, modelImages.length]);
  
  // Scroll handler for navbar animation - CHANGES HEADER ON SCROLL
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  // Navigate to page - normal navigation
  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Don't navigate if we're actively editing
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);
  
  // Handle navigation after Enter key in edit mode
  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  // Open blog modal
  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (p.previewEditMode && p.activeEditField) {
      // Deactivate inline edit first, then open modal after a short delay
      if (typeof p.onDeactivateField === 'function') {
        p.onDeactivateField();
      }
      setTimeout(() => {
        setSelectedPost(post);
        document.body.style.overflow = 'hidden';
      }, 50);
      return;
    }

    setSelectedPost(post);
    document.body.style.overflow = 'hidden';
  }, [p.previewEditMode, p.activeEditField, p.onDeactivateField]);

  // Close blog modal
  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
    document.body.style.overflow = 'auto';
  }, []);

  // Handle backdrop click
  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      closeBlogModal();
    }
  }, [p.previewEditMode, p.activeEditField, closeBlogModal]);
  
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' && selectedPost) {
        closeBlogModal();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [selectedPost, closeBlogModal]);
  
  const copy = getHeroContent(p, {
    eyebrow: "Elysian Collection",
    title: "Where elegance meets artistry",
    subtitle: "Discover timeless pieces crafted for the modern connoisseur. Each garment tells a story of sophistication and grace.",
    primaryCta: "Explore Collection",
    secondaryCta: "Our Story",
  });
  
  // Featured products with animations
  const featuredProducts = (p.allProducts || []).slice(0, 6);

  // Determine if we should use dark text based on scroll position and active page
  const shouldUseDarkText = isScrolled || activePage !== "home";

  // Handle View All Products click
  const handleViewAllProducts = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    // Navigate to shop page and set active tab to products
    setActivePage("shop");
    if (typeof p.setActiveTab === "function") {
      p.setActiveTab("products");
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField, p.setActiveTab]);

  return (
    <div key={refreshKey} className="min-h-screen flex flex-col bg-[#faf6f0] overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'Playfair Display', Georgia, serif" }}>
      
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-amber-200/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 -right-20 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-100/10 rounded-full blur-3xl animate-pulse delay-2000" />
      </div>
      
      {/* Animated Top Bar - Changes style on scroll */}
      <div className={`relative transition-all duration-500 ${
        isScrolled 
          ? "bg-gradient-to-r from-[#2d1b4e] via-[#c9a96e] to-[#2d1b4e] text-white py-2" 
          : "bg-gradient-to-r from-[#2d1b4e] via-[#c9a96e] to-[#2d1b4e] text-white py-2"
      } overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
        <div className="max-w-7xl mx-auto px-4 text-center text-sm font-light tracking-wider">
          ✨ <InlineEditableTextWithNav p={p} field="top_bar_message" value="FREE SHIPPING ON ORDERS OVER ₹5000 | LUXURY PACKAGING INCLUDED" onNavigate={handleNavigateAfterEdit} navPage="home" /> ✨
        </div>
      </div>
      
      {/* Animated Navigation - CHANGES ON SCROLL AND PAGE CHANGE */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${
        shouldUseDarkText 
          ? "bg-white/95 backdrop-blur-xl shadow-lg py-2 border-b border-gray-100" 
          : "bg-transparent py-4"
      }`}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg transition-colors"
          >
            <MdMenu size={24} className={shouldUseDarkText ? "text-[#2d1b4e]" : "text-white"} />
          </button>
          
          {/* Logo - Changes style on scroll and page change */}
          <div className="flex items-center gap-3 cursor-pointer transition-all duration-500" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border={`border-2 transition-all duration-500 ${
              shouldUseDarkText ? "border-[#c9a96e]" : "border-white/50"
            }`} />
            <div className="hidden sm:block">
              <p className={`font-bold tracking-wide transition-all duration-500 ${
                shouldUseDarkText ? "text-[#2d1b4e]" : "text-white"
              }`}>
                <InlineEditableTextWithNav p={p} field="businessName" value={p.storeInfo?.businessName || "Elysian"} onNavigate={handleNavigateAfterEdit} navPage="home" />
              </p>
              <p className={`text-xs transition-all duration-500 ${
                shouldUseDarkText ? "text-[#c9a96e]" : "text-white/80"
              }`}>
                <InlineEditableTextWithNav p={p} field="business_subtitle" value={p.storeInfo?.businessCategory || "Luxury Collection"} onNavigate={handleNavigateAfterEdit} navPage="home" />
              </p>
            </div>
          </div>
          
          {/* Desktop Navigation Links - Changes style on scroll and page change */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium tracking-wide transition-all duration-300 hover:scale-105 relative group ${
                  shouldUseDarkText 
                    ? `${activePage === item.page ? "text-[#c9a96e]" : "text-gray-700 hover:text-[#c9a96e]"}`
                    : `${activePage === item.page ? "text-[#c9a96e]" : "text-white/90 hover:text-white"}`
                }`}
              >
                <InlineEditableTextWithNav p={p} field={item.key} value={item.name} onNavigate={handleNavigateAfterEdit} navPage={item.page} />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-[#c9a96e] transition-all duration-300 group-hover:w-full ${
                  activePage === item.page ? "w-full" : "w-0"
                }`} />
              </button>
            ))}
          </div>
          
          <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="hidden md:block w-64" dark={!shouldUseDarkText} />
          
          <div className="flex items-center gap-3">
            <button onClick={p.handleGoToWishlist} className="relative group">
              <div className="p-2 rounded-full transition-all duration-300 group-hover:bg-white/10 group-hover:scale-110">
                <FaHeart className={`text-lg transition-all duration-300 ${
                  shouldUseDarkText ? "text-[#2d1b4e] group-hover:text-[#c9a96e]" : "text-white"
                }`} />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-r from-[#c9a96e] to-[#e8d5c4] text-[#2d1b4e] text-[10px] flex items-center justify-center font-bold animate-bounce">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={!shouldUseDarkText} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-4 px-4 flex flex-col gap-3 z-50 animate-slide-down">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium py-2 px-3 rounded-lg transition-all ${activePage === item.page ? "bg-[#c9a96e]/20 text-[#c9a96e]" : "text-gray-700"}`}
              >
                <InlineEditableTextWithNav p={p} field={item.key} value={item.name} onNavigate={handleNavigateAfterEdit} navPage={item.page} />
              </button>
            ))}
            <div className="pt-2 border-t border-gray-100">
              <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="w-full" />
            </div>
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE SECTION ==================== */}
      {activePage === "home" && (
        <section className="relative h-screen min-h-[700px] overflow-hidden -mt-[72px]">
          {/* Background Image with Ken Burns Effect */}
          <div className="absolute inset-0">
            {modelImages.map((img, idx) => (
              <div
                key={idx}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                  idx === activeImageIndex ? "opacity-100" : "opacity-0"
                }`}
                onMouseEnter={() => setIsHoveringHero(true)}
                onMouseLeave={() => setIsHoveringHero(false)}
              >
                <img
                  src={img}
                  alt={`Model ${idx + 1}`}
                  className="w-full h-full object-cover object-center animate-ken-burns"
                />
              </div>
            ))}
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#faf6f0] via-transparent to-transparent" />
          </div>
          
          {/* Floating Accent Elements */}
          <div className="absolute top-1/4 left-10 animate-float">
            <div className="w-16 h-16 border border-[#c9a96e]/40 rounded-full flex items-center justify-center backdrop-blur-sm">
              <div className="w-2 h-2 bg-[#c9a96e] rounded-full animate-ping" />
            </div>
          </div>
          <div className="absolute bottom-1/3 right-10 animate-float-delayed">
            <div className="w-24 h-24 border border-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <span className="text-white/60 text-xs tracking-wider">
                <InlineEditableTextWithNav p={p} field="floating_est_text" value="EST. 2024" onNavigate={handleNavigateAfterEdit} navPage="home" />
              </span>
            </div>
          </div>
          
          {/* Hero Content */}
          <div className="relative h-full max-w-7xl mx-auto px-4 flex flex-col justify-center">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
                <span className="w-2 h-2 rounded-full bg-[#c9a96e] animate-pulse" />
                <span className="text-xs tracking-[0.3em] text-white/90 font-light">
                  <InlineEditableTextWithNav p={p} field="heroEyebrow" value={copy.eyebrow} onNavigate={handleNavigateAfterEdit} navPage="home" />
                </span>
              </div>
              
              <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.1] tracking-tight">
                <InlineEditableTextWithNav p={p} field="heroTitle" value={copy.title} onNavigate={handleNavigateAfterEdit} navPage="home" />
              </h1>
              
              <p className="mt-6 text-white/80 text-lg max-w-lg">
                <InlineEditableTextWithNav p={p} field="heroSubtitle" value={copy.subtitle} onNavigate={handleNavigateAfterEdit} navPage="home" />
              </p>
              
              <div className="mt-10 flex gap-4">
                <button
                  onClick={(e) => navigateTo("shop", e)}
                  className="group px-8 py-4 rounded-full bg-gradient-to-r from-[#c9a96e] to-[#e8d5c4] text-[#2d1b4e] font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl flex items-center gap-2 overflow-hidden relative"
                >
                  <span className="relative z-10">
                    <InlineEditableTextWithNav p={p} field="primaryCtaText" value={copy.primaryCta} onNavigate={handleNavigateAfterEdit} navPage="shop" />
                  </span>
                  <span className="relative z-10 group-hover:translate-x-1 transition-transform duration-300">→</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#e8d5c4] to-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </button>
                
                <button
                  onClick={(e) => navigateTo("about", e)}
                  className="px-8 py-4 rounded-full border-2 border-white/30 text-white font-semibold tracking-wide transition-all duration-300 hover:border-white/60 hover:bg-white/10 backdrop-blur-sm"
                >
                  <InlineEditableTextWithNav p={p} field="secondaryCtaText" value={copy.secondaryCta} onNavigate={handleNavigateAfterEdit} navPage="about" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 rounded-full border-2 border-white/40 flex justify-center">
              <div className="w-1 h-2 bg-white/60 rounded-full mt-2 animate-scroll" />
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== SHOP SECTION ==================== */}
      {activePage === "shop" && (
        <section className="relative max-w-7xl mx-auto w-full px-4 py-12">
          <div className="text-center mb-12">
            <span className="text-[#c9a96e] text-sm tracking-wider uppercase">
              <InlineEditableTextWithNav p={p} field="shop_section_badge" value="Shop Collection" onNavigate={handleNavigateAfterEdit} navPage="shop" />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b4e] mt-2">
              <InlineEditableTextWithNav p={p} field="shop_section_title" value="Discover Our Pieces" onNavigate={handleNavigateAfterEdit} navPage="shop" />
            </h2>
            <div className="w-20 h-0.5 bg-gradient-to-r from-[#c9a96e] to-transparent mx-auto mt-4" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {shopCategories.map((item, idx) => (
              <div
                key={item.title}
                className="group bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={(e) => navigateTo("collections", e)}
              >
                <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${item.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <span className="text-2xl">{item.icon}</span>
                </div>
                <h3 className="text-lg font-semibold text-[#2d1b4e] mb-2">
                  <InlineEditableTextWithNav p={p} field={item.key} value={item.title} onNavigate={handleNavigateAfterEdit} navPage="collections" />
                </h3>
                <p className="text-sm text-gray-500">
                  <InlineEditableTextWithNav p={p} field={item.descKey} value={item.desc} onNavigate={handleNavigateAfterEdit} navPage="collections" />
                </p>
                <div className="mt-4 flex items-center gap-1 text-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-sm">
                    <InlineEditableTextWithNav p={p} field="explore_text" value="Explore" onNavigate={handleNavigateAfterEdit} navPage="collections" />
                  </span>
                  <span>→</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== COLLECTIONS SECTION ==================== */}
      {activePage === "collections" && featuredProducts.length > 0 && (
        <section className="max-w-7xl mx-auto w-full px-4 py-12">
          <div className="text-center mb-12">
            <span className="text-[#c9a96e] text-sm tracking-wider uppercase">
              <InlineEditableTextWithNav p={p} field="collections_badge" value="Curated Selection" onNavigate={handleNavigateAfterEdit} navPage="collections" />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b4e] mt-2">
              <InlineEditableTextWithNav p={p} field="collections_title" value="Featured Pieces" onNavigate={handleNavigateAfterEdit} navPage="collections" />
            </h2>
            <div className="w-20 h-0.5 bg-gradient-to-r from-[#c9a96e] to-transparent mx-auto mt-4" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredProducts.map((product, idx) => (
              <div
                key={product._id}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
              >
                <div className="relative overflow-hidden h-72">
                  {product.productImage || product.thumbnail ? (
                    <img
                      src={AWS_URL + (product.productImage || product.thumbnail)}
                      alt={product.productName}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#2d1b4e]/10 to-[#c9a96e]/10 flex items-center justify-center">
                      <span className="text-6xl">✨</span>
                    </div>
                  )}
                  {product.discount > 0 && (
                    <div className="absolute top-4 right-4 bg-[#c9a96e] text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
                      <InlineEditableTextWithNav p={p} field={`discount_text_${product._id}`} value={`${product.discount}% OFF`} onNavigate={handleNavigateAfterEdit} navPage="collections" isInModal={true} />
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-semibold text-[#2d1b4e] line-clamp-1">
                    <InlineEditableTextWithNav p={p} field={`product_${product._id}_name`} value={product.productName} onNavigate={handleNavigateAfterEdit} navPage="collections" isInModal={true} />
                  </h3>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                    <InlineEditableTextWithNav p={p} field={`product_${product._id}_desc`} value={product.description || "Luxury piece crafted with attention to detail"} onNavigate={handleNavigateAfterEdit} navPage="collections" isInModal={true} />
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <div>
                      <span className="text-xl font-bold text-[#c9a96e]">
                        <InlineEditableTextWithNav p={p} field={`product_${product._id}_price`} value={`₹${product.salePrice}`} onNavigate={handleNavigateAfterEdit} navPage="collections" isInModal={true} />
                      </span>
                      {product.mrp > product.salePrice && (
                        <span className="text-sm text-gray-400 line-through ml-2">
                          <InlineEditableTextWithNav p={p} field={`product_${product._id}_mrp`} value={`₹${product.mrp}`} onNavigate={handleNavigateAfterEdit} navPage="collections" isInModal={true} />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <button
              onClick={handleViewAllProducts}
              className="group px-8 py-3 rounded-full border-2 border-[#c9a96e] text-[#c9a96e] font-semibold hover:bg-[#c9a96e] hover:text-white transition-all duration-300 flex items-center gap-2 mx-auto"
            >
              <InlineEditableTextWithNav p={p} field="view_all_products_button" value="View All Products" onNavigate={handleNavigateAfterEdit} navPage="collections" />
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </button>
          </div>
        </section>
      )}
      
      {/* ==================== JOURNAL SECTION ==================== */}
      {activePage === "journal" && (
        <section className="max-w-7xl mx-auto w-full px-4 py-12">
          <div className="text-center mb-12">
            <span className="text-[#c9a96e] text-sm tracking-wider uppercase">
              <InlineEditableTextWithNav p={p} field="journal_badge" value="Our Journal" onNavigate={handleNavigateAfterEdit} navPage="journal" />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b4e] mt-2">
              <InlineEditableTextWithNav p={p} field="journal_title" value="Latest Stories" onNavigate={handleNavigateAfterEdit} navPage="journal" />
            </h2>
            <div className="w-20 h-0.5 bg-gradient-to-r from-[#c9a96e] to-transparent mx-auto mt-4" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, idx) => (
              <div key={post.id} className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer">
                <div className="h-56 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#c9a96e] font-semibold">
                      <InlineEditableTextWithNav p={p} field={post.categoryKey} value={post.category} onNavigate={handleNavigateAfterEdit} navPage="journal" />
                    </span>
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav p={p} field={post.readTimeKey} value={post.readTime} onNavigate={handleNavigateAfterEdit} navPage="journal" />
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mb-2">
                    <InlineEditableTextWithNav p={p} field={post.dateKey} value={post.date} onNavigate={handleNavigateAfterEdit} navPage="journal" />
                  </p>
                  <h3 className="text-xl font-semibold text-[#2d1b4e] mb-2">
                    <InlineEditableTextWithNav p={p} field={post.titleKey} value={post.title} onNavigate={handleNavigateAfterEdit} navPage="journal" />
                  </h3>
                  <p className="text-gray-500 text-sm line-clamp-2">
                    <InlineEditableTextWithNav p={p} field={post.descKey} value={post.desc} onNavigate={handleNavigateAfterEdit} navPage="journal" />
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav p={p} field="by_prefix" value="By" onNavigate={handleNavigateAfterEdit} navPage="journal" />{' '}
                      <InlineEditableTextWithNav p={p} field={post.authorKey} value={post.author} onNavigate={handleNavigateAfterEdit} navPage="journal" />
                    </span>
                    <button 
                      onClick={(e) => openBlogModal(post, e)}
                      className="text-[#c9a96e] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      <InlineEditableTextWithNav p={p} field="read_more_button" value="Read More" onNavigate={handleNavigateAfterEdit} navPage="journal" />
                      <span>→</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT SECTION ==================== */}
      {activePage === "about" && (
        <section className="max-w-7xl mx-auto w-full px-4 py-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#c9a96e] text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav p={p} field="about_badge" value="Our Story" onNavigate={handleNavigateAfterEdit} navPage="about" />
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#2d1b4e] mt-3">
                <InlineEditableTextWithNav p={p} field="about_title" value="A Legacy of Elegance" onNavigate={handleNavigateAfterEdit} navPage="about" />
              </h2>
              <div className="w-16 h-0.5 bg-[#c9a96e] mt-4 mb-6" />
              <p className="text-gray-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav p={p} field="about_para1" value="Founded in 2024, Elysian represents the pinnacle of luxury fashion. Each piece is meticulously crafted by master artisans who pour their heart and soul into every stitch." onNavigate={handleNavigateAfterEdit} navPage="about" />
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav p={p} field="about_para2" value="Our commitment to excellence extends beyond our products to every interaction you have with our brand. From personalized styling consultations to the luxurious packaging, we ensure that every touchpoint reflects our dedication to sophistication and grace." onNavigate={handleNavigateAfterEdit} navPage="about" />
              </p>
              <div className="flex gap-6">
                <div>
                  <p className="text-3xl font-bold text-[#c9a96e]">
                    <InlineEditableTextWithNav p={p} field="about_stat1_value" value="200+" onNavigate={handleNavigateAfterEdit} navPage="about" />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav p={p} field="about_stat1_label" value="Hours per gown" onNavigate={handleNavigateAfterEdit} navPage="about" />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#c9a96e]">
                    <InlineEditableTextWithNav p={p} field="about_stat2_value" value="50+" onNavigate={handleNavigateAfterEdit} navPage="about" />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav p={p} field="about_stat2_label" value="Master artisans" onNavigate={handleNavigateAfterEdit} navPage="about" />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#c9a96e]">
                    <InlineEditableTextWithNav p={p} field="about_stat3_value" value="15+" onNavigate={handleNavigateAfterEdit} navPage="about" />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav p={p} field="about_stat3_label" value="Countries served" onNavigate={handleNavigateAfterEdit} navPage="about" />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&q=80" 
                alt="About Elysian" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== TABS SECTION ==================== */}
      {(activePage === "shop" || activePage === "collections") && (
        <section className="max-w-7xl mx-auto w-full px-4 mt-8">
          <div className="flex justify-center gap-1 p-1 bg-white/50 backdrop-blur-sm rounded-full border border-[#c9a96e]/20 w-fit mx-auto">
            {[
              ["products", getTabLabel(p, "products"), <MdList key="p" />],
              ["services", getTabLabel(p, "services"), <MdList key="s" />],
              ["details", getTabLabel(p, "details", "store-info"), <MdStore key="d" />],
            ].map(([key, label, icon]) => (
              <button
                key={key}
                onClick={() => {
                  if (p.previewEditMode && p.activeEditField) return;
                  if (typeof p.setActiveTab === "function") {
                    p.setActiveTab(key);
                  }
                }}
                className={`px-6 py-3 rounded-full text-sm font-semibold flex items-center gap-2 transition-all duration-300 ${
                  p.activeTab === key
                    ? "bg-gradient-to-r from-[#2d1b4e] to-[#c9a96e] text-white shadow-lg scale-105"
                    : "text-gray-500 hover:text-[#2d1b4e]"
                }`}
              >
                {icon}
                <InlineEditableTextWithNav p={p} field={`tab_${key}_label`} value={label} onNavigate={handleNavigateAfterEdit} navPage={activePage} />
              </button>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== MAIN CONTENT ==================== */}
      {(activePage === "shop" || activePage === "collections") && (
        <section className="flex-1 max-w-7xl mx-auto w-full px-4 py-12">
          <TabContent p={p} />
        </section>
      )}
      
      {/* ==================== NEWSLETTER SECTION ==================== */}
      {activePage !== "about" && (
        <section className="relative bg-gradient-to-r from-[#2d1b4e] to-[#1a1a2e] py-16 mt-8 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-64 h-64 bg-[#c9a96e] rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>
          <div className="max-w-3xl mx-auto text-center px-4 relative z-10">
            <div className="inline-block px-4 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-4">
              <span className="text-xs tracking-wider text-[#c9a96e]">
                <InlineEditableTextWithNav p={p} field="newsletter_badge" value="SUBSCRIBE" onNavigate={handleNavigateAfterEdit} navPage="home" />
              </span>
            </div>
            <h3 className="text-3xl font-bold text-white mb-3">
              <InlineEditableTextWithNav p={p} field="newsletter_title" value="Join the Elysian Circle" onNavigate={handleNavigateAfterEdit} navPage="home" />
            </h3>
            <p className="text-white/70 mb-6">
              <InlineEditableTextWithNav p={p} field="newsletter_subtitle" value="Be the first to discover new arrivals, exclusive offers, and behind-the-scenes stories." onNavigate={handleNavigateAfterEdit} navPage="home" />
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder={p.storefrontText?.email_placeholder || "Your email address"}
                className="flex-1 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-[#c9a96e] transition-colors"
              />
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!(p.previewEditMode && p.activeEditField)) {
                    // Handle newsletter subscription
                  }
                }}
                className="px-6 py-3 rounded-full bg-[#c9a96e] text-[#2d1b4e] font-semibold hover:bg-[#e8d5c4] transition-colors"
              >
                <InlineEditableTextWithNav p={p} field="subscribe_button" value="Subscribe" onNavigate={handleNavigateAfterEdit} navPage="home" />
              </button>
            </div>
          </div>
        </section>
      )}
      
      <StoreFooter p={p} />
      
      {/* ==================== BLOG POST MODAL ==================== */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-fade-in" onClick={handleBackdropClick}>
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-scale-in" onClick={e => e.stopPropagation()}>
            <div className="relative h-64 md:h-80">
              <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-full object-cover" />
              <button 
                onClick={closeBlogModal}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-[#c9a96e] transition-all duration-300 flex items-center justify-center text-xl"
              >
                ✕
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                <div className="flex items-center gap-3 text-white">
                  <span className="text-xs bg-[#c9a96e] px-2 py-1 rounded-full">
                    <InlineEditableTextWithNav p={p} field={selectedPost.categoryKey} value={selectedPost.category} onNavigate={handleNavigateAfterEdit} navPage="journal" isInModal={true} />
                  </span>
                  <span className="text-xs">
                    <InlineEditableTextWithNav p={p} field={selectedPost.readTimeKey} value={selectedPost.readTime} onNavigate={handleNavigateAfterEdit} navPage="journal" isInModal={true} />
                  </span>
                </div>
              </div>
            </div>
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-400">
                  <InlineEditableTextWithNav p={p} field={selectedPost.dateKey} value={selectedPost.date} onNavigate={handleNavigateAfterEdit} navPage="journal" isInModal={true} />
                </span>
                <span className="text-sm text-gray-400">
                  <InlineEditableTextWithNav p={p} field="by_prefix" value="By" onNavigate={handleNavigateAfterEdit} navPage="journal" isInModal={true} />{' '}
                  <InlineEditableTextWithNav p={p} field={selectedPost.authorKey} value={selectedPost.author} onNavigate={handleNavigateAfterEdit} navPage="journal" isInModal={true} />
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#2d1b4e] mb-4">
                <InlineEditableTextWithNav p={p} field={selectedPost.titleKey} value={selectedPost.title} onNavigate={handleNavigateAfterEdit} navPage="journal" isInModal={true} />
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  <InlineEditableTextWithNav p={p} field={selectedPost.fullContentKey} value={selectedPost.fullContent} onNavigate={handleNavigateAfterEdit} navPage="journal" isInModal={true} />
                </p>
              </div>
              <div className="mt-8 pt-6 border-t border-gray-100 flex justify-between items-center">
                <button 
                  onClick={closeBlogModal}
                  className="px-6 py-2 rounded-full border border-[#c9a96e] text-[#c9a96e] hover:bg-[#c9a96e] hover:text-white transition-all"
                >
                  <InlineEditableTextWithNav p={p} field="close_button" value="Close" onNavigate={handleNavigateAfterEdit} navPage="journal" isInModal={true} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes ken-burns {
          0% { transform: scale(1); }
          100% { transform: scale(1.05); }
        }
        .animate-ken-burns {
          animation: ken-burns 20s ease-out forwards;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float 6s ease-in-out infinite 3s;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
        @keyframes char-appear {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-char-appear {
          animation: char-appear 0.5s cubic-bezier(0.2, 0.9, 0.4, 1.1) forwards;
          opacity: 0;
        }
        @keyframes scroll {
          0% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(10px); }
        }
        .animate-scroll {
          animation: scroll 1.5s ease-in-out infinite;
        }
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out forwards;
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}